import type { ReactNode } from 'react';

import { Card } from '@/components/ui';
import type { EntertainmentActionTask } from '@/lib/entertainment/ledger-types';
import { classNames, formatCurrency } from '@/lib/utils';

type Props = {
  currentUserId: string;
  canAcknowledgeAny?: boolean;
  tasks: EntertainmentActionTask[];
  userNames: Record<string, string>;
  onAcknowledge: (paymentTransactionId: string) => void | Promise<void>;
  onReject: (paymentTransactionId: string) => void | Promise<void>;
};

function PaymentAckIconButton({
  label,
  tone,
  onClick,
  children,
}: {
  label: string;
  tone: 'reject' | 'acknowledge';
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className={classNames(
        'flex h-12 w-12 shrink-0 items-center justify-center rounded-full border text-2xl font-black leading-none shadow-sm transition-[background-color,border-color,color,box-shadow,transform] duration-200 hover:scale-105 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        tone === 'reject'
          ? 'border-status-error bg-status-error text-white hover:border-status-error/90 hover:bg-status-error/90 focus-visible:ring-status-error/40'
          : 'border-status-success bg-status-success text-white hover:border-status-success/90 hover:bg-status-success/90 focus-visible:ring-status-success/40',
      )}
    >
      <span aria-hidden="true">{children}</span>
    </button>
  );
}

export function PaymentAcknowledgementPanel({
  currentUserId,
  canAcknowledgeAny = false,
  tasks,
  userNames,
  onAcknowledge,
  onReject,
}: Props) {
  const openTasks = tasks.filter((task) =>
    task.task_type === 'entertainment_payment_acknowledgement'
    && task.status === 'open'
    && (canAcknowledgeAny || task.target_user_id === currentUserId)
  );

  return (
    <Card className="p-4">
      <div className="flex flex-col gap-3">
        <h2 className="text-sm font-bold text-aura-primary mb-3 uppercase tracking-widest">Payment Acknowledgements</h2>
        {openTasks.length === 0 ? (
          <div className="py-8 text-center text-sm font-semibold text-aura-primary/40">No payments waiting for your acknowledgement.</div>
        ) : (
          openTasks.map((task) => {
            const payerName = userNames[task.payer_id] ?? task.payer_id;
            const payeeName = userNames[task.payee_id] ?? task.payee_id;
            return (
              <div
                key={task.id}
                data-testid="payment-ack-task"
                data-payment-transaction-id={task.payment_transaction_id}
                className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-xl border border-border-subtle border-l-[3px] border-l-aura-primary/20 bg-surface p-3 transition-colors hover:bg-aura-primary/[0.03]"
              >
                <div
                  data-testid="payment-ack-summary"
                  className="min-w-0 whitespace-normal break-words text-sm font-medium leading-snug text-text-main"
                >
                  {formatCurrency(task.amount_cents / 100)} payment from {payerName} to {payeeName}
                </div>
                <div className="flex shrink-0 items-center justify-end gap-2">
                  <PaymentAckIconButton
                    tone="reject"
                    label={`Reject payment from ${payerName} to ${payeeName}`}
                    onClick={() => onReject(task.payment_transaction_id)}
                  >
                    X
                  </PaymentAckIconButton>
                  <PaymentAckIconButton
                    tone="acknowledge"
                    label={`Acknowledge payment from ${payerName} to ${payeeName}`}
                    onClick={() => onAcknowledge(task.payment_transaction_id)}
                  >
                    &#10003;
                  </PaymentAckIconButton>
                </div>
              </div>
            );
          })
        )}
      </div>
    </Card>
  );
}
